import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Line } from "react-chartjs-2";
import socket from './socket'
import { Chart as ChartJS } from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import DrawIcon from '@mui/icons-material/Draw';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';


ChartJS.register(zoomPlugin);
ChartJS.register(annotationPlugin);

// StockLineChart Component
function StockLineChart({ chartData, stockLinesData, drawnLinesData }) {
    return (
        <Line
            data={{
                datasets: [
                    ...chartData.datasets,
                    ...stockLinesData,
                    ...drawnLinesData,
                ],
            }}
            options={{
                responsive: true,
                plugins: {
                    zoom: {
                        pan: {
                            enabled: true,
                            mode: 'xy',
                            speed: 10,
                            threshold: 10
                        },
                        zoom: {
                            wheel: {
                                enabled: true
                            },
                            pinch: {
                                enabled: true
                            },
                            mode: 'xy'
                        },
                    },
                },
            }}
        />
    );
}

// make StockLineChart memoized to avoid unnecessary re-renders
const MemoizedStockLineChart = React.memo(StockLineChart);

// DrawingArea Component
function DrawingArea({ canvasRef, width, height, onMouseDown, onMouseUp, onMouseMove, isDrawingEnabled }) {
    useEffect(() => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.canvas.width = width;
        ctx.canvas.height = height;
    }, [canvasRef, width, height]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: '0',
                left: '0',
                width,
                height,
                border: "1px solid #000000",
                pointerEvents: isDrawingEnabled ? 'auto' : 'none',
            }}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
        />
    );
}

function usePrevious(value) {
    const ref = useRef();

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

// Main LineChart component
function LineChart({ chartData, roomId }) {
    const indicators = ['SMA', 'EMA', 'WMA', 'DEMA', 'TEMA', 'TRIMA', 'KAMA', 'MAMA', 'VWAP', 'T3', 'MACD', 'MACDEXT', 'STOCH', 'STOCHF', 'RSI',
        'STOCHRSI', 'WILLR', 'ADX', 'ADXR', 'APO', 'PPO', 'MOM', 'BOP', 'CCI', 'CMO', 'ROC', 'ROCR', 'AROON', 'AROONOSC', 'MFI',
        'TRIX', 'ULTOSC', 'DX', 'MINUS_DI', 'PLUS_DI', 'MINUS_DM', 'PLUS_DM', 'BBANDS', 'MIDPOINT', 'MIDPRICE', 'SAR', 'TRANGE',
        'ATR', 'NATR', 'AD', 'ADOSC', 'OBV', 'HT_TRENDLINE', 'HT_SINE', 'HT_TRENDMODE', 'HT_DCPERIOD', 'HT_DCPHASE', 'HTPHASOR'];

    const canvasRef = useRef();
    const previousSocket = usePrevious(socket);
    const previousRoomId = usePrevious(roomId);
    const containerRef = useRef();
    const drawingRef = useRef(false);
    const startPointRef = useRef({ x: 0, y: 0 });
    const [stock, setStock] = useState('IBM');
    const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
    const [points, setPoints] = useState([]);
    const [isDrawingEnabled, setDrawingEnabled] = useState(true);
    const [selectedIndicator, setSelectedIndicator] = useState("");
    const [stockLinesData, setStockLinesData] = useState(chartData.datasets);
    const [drawnLinesData, setDrawnLinesData] = useState([]);
    const theme = useTheme();
    const handleIndicatorChange = (event) => {
        const selected = event.target.value;
        setSelectedIndicator(selected);

        // Emit socket event with the selected indicator and room ID
        socket.emit('selected_indicator_changed', { selectedIndicator: selected, roomId });
    };



    const handleStockChange = (event) => {
        setStock(event.target.value);
    };

    const sendStock = () => {
        socket.emit('send_stock', { stock, roomId });
        fetchIndicatorData('Original', stock)
    };

    useEffect(() => {
        if (socket) {
            socket.on('set_send_stock', (data) => {
                console.log(data.roomId)
                if (data.roomId === roomId) {
                    setStock(data.stock);
                    console.log(data.stock)
                    fetchIndicatorData("Original", data.stock)
                }
            });
        }
        return () => {
            if (socket) {
                socket.off('set_send_stock');
            }
        };
    }, [socket, roomId]);



    useEffect(() => {
        if (socket) {
            socket.on('selected_indicator_changed', (data) => {
                if (data.roomId === roomId) {
                    console.log(selectedIndicator)
                    setSelectedIndicator(data.selectedIndicator);

                }
            });
        }

        return () => {
            if (socket) {
                socket.off('selected_indicator_changed');
            }
        };
    }, [socket, roomId]);

    useEffect(() => {
        const updateDimensions = () => {
            setDimensions({
                height: containerRef.current.offsetHeight,
                width: containerRef.current.offsetWidth,
            });
        };
        updateDimensions();
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    useEffect(() => {
        if (socket !== previousSocket) {
            console.log("useEffect triggered by socket change");
        }

        if (roomId !== previousRoomId) {
            console.log("useEffect triggered by roomId change");
        }
        if (socket) {
            socket.on('new_line_drawn', (data) => {
                console.log("recieved event")
                if (data.roomId === roomId) {
                    const points = data.points;
                    for (let i = 0; i < points.length - 1; i++) {
                        drawLine(points[i], points[i + 1]);
                    }
                }
            });
        }

        return () => {
            if (socket) {
                socket.off('new_line_drawn');
            }
        };
    }, [socket, roomId]);

    useEffect(() => {
        if (selectedIndicator) {
            fetchIndicatorData(selectedIndicator, stock);
        }
    }, [selectedIndicator]);

    const handleMouseDown = (event) => {
        if (!isDrawingEnabled) return;
        const currentPosition = getMousePosition(event);
        startPointRef.current = currentPosition;
        drawingRef.current = true;
    };

    const handleMouseUp = () => {
        if (!isDrawingEnabled) return;
        drawingRef.current = false;
        const lineData = {
            points,
            roomId,
        };
        socket.emit('line_drawn', lineData);
        setPoints([]);
    };

    const handleMouseMove = (event) => {
        if (!isDrawingEnabled) return;
        if (!drawingRef.current) return;
        const currentPosition = getMousePosition(event);
        drawLine(startPointRef.current, currentPosition);
        startPointRef.current = currentPosition;
        setPoints(prevPoints => [...prevPoints, currentPosition]);
    };

    const toggleDrawing = () => {
        setDrawingEnabled(!isDrawingEnabled);
    };

    const getMousePosition = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // calculate the CSS to canvas pixel ratio
        const xRatio = canvas.width / rect.width;
        const yRatio = canvas.height / rect.height;

        return {
            x: (event.clientX - rect.left) * xRatio,
            y: (event.clientY - rect.top) * yRatio,
        };
    };

    const drawLine = (start, end) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
    };



    const fetchIndicatorData = async (indicator, current_stock) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACK_END_URL}/stocks/${indicator}/${current_stock}`);
            const data = await response.json();
            const { stocks, days, prices } = data;
            const newLineData = {
                label: indicator,
                data: days.map((day, index) => ({
                    x: day,
                    y: prices[index],
                })),
                fill: false,
                borderColor: getRandomColor(),
            };
            setStockLinesData(prevLinesData => [...prevLinesData, newLineData]);
        } catch (error) {
            console.error('Failed to fetch indicator data:', error);
        }
    };

    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    return (
        <div ref={containerRef} style={{ position: 'relative', height: '500px', width: '1000px' }}>
            <TextField
                required
                id="outlined-required"
                label="Stock Ticker"
                defaultValue={stock}
                value={stock} onChange={handleStockChange}
                InputProps={{
                    style: { color: theme.palette.text.secondary },
                }}

            />
            {/* <input type="text" value={stock} onChange={handleStockChange} placeholder="Enter stock ticker" /> */}
            <IconButton onClick={sendStock}><AddCircleOutlinedIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} /></IconButton>

            <div style={{ position: 'relative', height: '100%', width: '100%', marginTop: '10px' }}>
                <MemoizedStockLineChart
                    chartData={chartData}
                    stockLinesData={stockLinesData}
                    drawnLinesData={drawnLinesData}
                />
                <DrawingArea
                    canvasRef={canvasRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    isDrawingEnabled={isDrawingEnabled}
                />
            </div>
            {isDrawingEnabled && (<Button onClick={toggleDrawing} variant="outlined" sx={{ color: theme.palette.primary.main, mt: "-1px", mr: "5px" }} endIcon={<HighlightAltIcon />}>Disable Canvas </Button>)}
            {!isDrawingEnabled && (<Button onClick={toggleDrawing} variant="outlined" sx={{ color: theme.palette.primary.main, mt: "-1px", mr: "5px", }} endIcon={<DrawIcon />}>Enable Canvas </Button>)}


            <Select labelId="demo-simple-select-label"
                id="demo-simple-select" label="Select Indicator" value={selectedIndicator} sx={{ color: theme.palette.primary.main, width: "120px", height: "37px", mt: "15px" }} onChange={handleIndicatorChange}>
                <MenuItem value="" sx={{ color: theme.palette.primary.main, }}>None</MenuItem>
                {indicators.map((indicator, index) => (
                    <MenuItem sx={{ color: theme.palette.primary.main, }} key={index} value={indicator}>{indicator}</MenuItem>
                ))}
            </Select>
        </div>
    );
}

export default LineChart;
